<template>
  <div v-show="isProjectLayerVisibleOnMap" class="MyMapProjectFilters">
    <div class="MyMapProjectFilters__titleBar" role="presentation">
      <span class="MyMapProjectFilters__titleText">{{
        $t('myMap.leftSidebar.projectFilters.filterBar')
      }}</span>
    </div>
    <MyMapPlatformItemSearch :item-type="ItemType.PROJECT" />
    <div class="MyMapProjectFilters__fields">
      <label class="MyMapProjectFilters__label">
        <span class="MyMapProjectFilters__fieldCaption">{{
          $t('myMap.leftSidebar.projectFilters.sector')
        }}</span>
        <select
          v-model="myMapStore.projectMapFilters.sector"
          :class="[
            'MyMapProjectFilters__input',
            { 'MyMapProjectFilters__input--empty': !myMapStore.projectMapFilters.sector },
          ]"
        >
          <option value="">{{ $t('myMap.leftSidebar.projectFilters.optionAll') }}</option>
          <option v-for="opt in projectFilterOptions.sector" :key="opt" :value="opt">
            {{ opt }}
          </option>
        </select>
      </label>
      <label class="MyMapProjectFilters__label">
        <span class="MyMapProjectFilters__fieldCaption">{{
          $t('myMap.leftSidebar.projectFilters.fundingType')
        }}</span>
        <select
          v-model="myMapStore.projectMapFilters.fundingType"
          :class="[
            'MyMapProjectFilters__input',
            { 'MyMapProjectFilters__input--empty': !myMapStore.projectMapFilters.fundingType },
          ]"
        >
          <option value="">{{ $t('myMap.leftSidebar.projectFilters.optionAll') }}</option>
          <option v-for="opt in projectFilterOptions.fundingType" :key="opt" :value="opt">
            {{ financingTypeLabel(opt) }}
          </option>
        </select>
      </label>
      <label class="MyMapProjectFilters__label">
        <span class="MyMapProjectFilters__fieldCaption">{{
          $t('myMap.leftSidebar.projectFilters.economicNature')
        }}</span>
        <select
          v-model="myMapStore.projectMapFilters.economicNature"
          :class="[
            'MyMapProjectFilters__input',
            { 'MyMapProjectFilters__input--empty': !myMapStore.projectMapFilters.economicNature },
          ]"
        >
          <option value="">{{ $t('myMap.leftSidebar.projectFilters.optionAll') }}</option>
          <option v-for="opt in projectFilterOptions.economicNature" :key="opt" :value="opt">
            {{ opt }}
          </option>
        </select>
      </label>
      <label class="MyMapProjectFilters__label">
        <span class="MyMapProjectFilters__fieldCaption">{{
          $t('myMap.leftSidebar.projectFilters.attributionInstance')
        }}</span>
        <select
          v-model="myMapStore.projectMapFilters.attributionInstance"
          :class="[
            'MyMapProjectFilters__input',
            {
              'MyMapProjectFilters__input--empty':
                !myMapStore.projectMapFilters.attributionInstance,
            },
          ]"
        >
          <option value="">{{ $t('myMap.leftSidebar.projectFilters.optionAll') }}</option>
          <option v-for="opt in projectFilterOptions.attributionInstance" :key="opt" :value="opt">
            {{ opt }}
          </option>
        </select>
      </label>
      <label class="MyMapProjectFilters__label">
        <span class="MyMapProjectFilters__fieldCaption">{{
          $t('myMap.leftSidebar.projectFilters.status')
        }}</span>
        <select
          v-model="myMapStore.projectMapFilters.status"
          :class="[
            'MyMapProjectFilters__input',
            { 'MyMapProjectFilters__input--empty': !myMapStore.projectMapFilters.status },
          ]"
        >
          <option value="">{{ $t('myMap.leftSidebar.projectFilters.optionAll') }}</option>
          <option v-for="opt in projectFilterOptions.status" :key="opt" :value="opt">
            {{ projectStatusLabel(opt) }}
          </option>
        </select>
      </label>
      <label class="MyMapProjectFilters__label">
        <span class="MyMapProjectFilters__fieldCaption">{{
          $t('myMap.leftSidebar.projectFilters.conventionAvailable')
        }}</span>
        <select
          v-model="myMapStore.projectMapFilters.conventionAvailable"
          :class="[
            'MyMapProjectFilters__input',
            {
              'MyMapProjectFilters__input--empty':
                myMapStore.projectMapFilters.conventionAvailable === '',
            },
          ]"
        >
          <option value="">{{ $t('myMap.leftSidebar.projectFilters.optionAll') }}</option>
          <option
            v-for="opt in projectFilterOptions.conventionAvailable"
            :key="String(opt)"
            :value="String(opt)"
          >
            {{
              opt
                ? $t('myMap.leftSidebar.projectFilters.conventionYes')
                : $t('myMap.leftSidebar.projectFilters.conventionNo')
            }}
          </option>
        </select>
      </label>
      <div class="MyMapProjectFilters__sliderBlock">
        <span class="MyMapProjectFilters__fieldCaption">{{
          $t('myMap.leftSidebar.projectFilters.fundingYear')
        }}</span>
        <div class="MyMapProjectFilters__sliderValues">{{ yearLow }} — {{ yearHigh }}</div>
        <DualRangeSlider
          :min-bound="yearMinBound"
          :max-bound="yearMaxBound"
          :low="yearLow"
          :high="yearHigh"
          :step="1"
          :aria-label-low="$t('myMap.leftSidebar.projectFilters.sliderMin')"
          :aria-label-high="$t('myMap.leftSidebar.projectFilters.sliderMax')"
          @update:low="onYearLowUpdate"
          @update:high="onYearHighUpdate"
        />
      </div>
      <div class="MyMapProjectFilters__sliderBlock">
        <span class="MyMapProjectFilters__fieldCaption">{{
          $t('myMap.leftSidebar.projectFilters.fundingAmount')
        }}</span>
        <div class="MyMapProjectFilters__sliderValues">
          {{ formatAmount(amountLow) }} — {{ formatAmount(amountHigh) }}
        </div>
        <DualRangeSlider
          :min-bound="amountMinBound"
          :max-bound="amountMaxBound"
          :low="amountLow"
          :high="amountHigh"
          :step="FUNDING_AMOUNT_SLIDER_STEP"
          :aria-label-low="$t('myMap.leftSidebar.projectFilters.sliderMin')"
          :aria-label-high="$t('myMap.leftSidebar.projectFilters.sliderMax')"
          @update:low="onAmountLowUpdate"
          @update:high="onAmountHighUpdate"
        />
      </div>
    </div>
    <button type="button" class="MyMapProjectFilters__clear" @click="onClearFilters">
      {{ $t('myMap.leftSidebar.projectFilters.clear') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ItemType } from '@/models/enums/app/ItemType'
import {
  createEmptyProjectFilterOptions,
  type ProjectFilterOptions,
} from '@/models/interfaces/map/PlatformFilterOptions'
import DualRangeSlider from '@/components/ui/DualRangeSlider.vue'
import MyMapPlatformItemSearch from '@/views/map/components/MyMapPlatformItemSearch.vue'
import { AppLayersService } from '@/services/map/AppLayersService'
import { ProjectService } from '@/services/projects/ProjectService'
import { useMyMapStore } from '@/stores/myMapStore'
import { useI18n } from 'vue-i18n'
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'

const myMapStore = useMyMapStore()
const { t, te } = useI18n()

const projectFilterOptions = reactive<ProjectFilterOptions>(createEmptyProjectFilterOptions())

const isProjectLayerVisibleOnMap = computed(() => myMapStore.projectLayer?.isShown === true)

const projectFilterOptionsLoaded = ref(false)

const currentCalendarYear = new Date().getFullYear()

const yearMinBound = computed(() => {
  const years = projectFilterOptions.fundingYear
  if (years.length === 0) return currentCalendarYear - 40
  return Math.min(...years)
})

const yearMaxBound = computed(() => {
  const years = projectFilterOptions.fundingYear
  if (years.length === 0) return currentCalendarYear + 2
  return Math.max(...years)
})

const amountMinBound = computed(() => {
  const amounts = projectFilterOptions.fundingAmount
  if (amounts.length === 0) return 0
  return Math.min(...amounts)
})

const amountMaxBound = computed(() => {
  const amounts = projectFilterOptions.fundingAmount
  if (amounts.length === 0) return 1
  return Math.max(...amounts)
})

/** Native range + client snap step for the funding amount dual slider. */
const FUNDING_AMOUNT_SLIDER_STEP = 1000

const yearLow = ref(0)
const yearHigh = ref(0)
const amountLow = ref(0)
const amountHigh = ref(0)

const amountFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 })

function formatAmount(value: number): string {
  return amountFormatter.format(value)
}

function projectStatusLabel(value: string): string {
  const key = `projects.status.${value}`
  return te(key) ? String(t(key)) : value
}

function financingTypeLabel(value: string): string {
  const key = `projects.financing.${value}`
  return te(key) ? String(t(key)) : value
}

function syncYearRangeToStore(): void {
  const minB = yearMinBound.value
  const maxB = yearMaxBound.value
  if (yearLow.value <= minB && yearHigh.value >= maxB) {
    myMapStore.projectMapFilters.fundingYearMin = ''
    myMapStore.projectMapFilters.fundingYearMax = ''
  } else {
    myMapStore.projectMapFilters.fundingYearMin = String(yearLow.value)
    myMapStore.projectMapFilters.fundingYearMax = String(yearHigh.value)
  }
}

function syncAmountRangeToStore(): void {
  if (projectFilterOptions.fundingAmount.length === 0) {
    return
  }
  const minB = amountMinBound.value
  const maxB = amountMaxBound.value
  if (amountLow.value <= minB && amountHigh.value >= maxB) {
    myMapStore.projectMapFilters.fundingAmountMin = ''
    myMapStore.projectMapFilters.fundingAmountMax = ''
  } else {
    myMapStore.projectMapFilters.fundingAmountMin = String(amountLow.value)
    myMapStore.projectMapFilters.fundingAmountMax = String(amountHigh.value)
  }
}

function resetYearSlidersToBounds(): void {
  yearLow.value = yearMinBound.value
  yearHigh.value = yearMaxBound.value
  syncYearRangeToStore()
}

function snapFundingAmountToStep(value: number): number {
  return Math.round(value / FUNDING_AMOUNT_SLIDER_STEP) * FUNDING_AMOUNT_SLIDER_STEP
}

function resetAmountSlidersToBounds(): void {
  const minB = amountMinBound.value
  const maxB = amountMaxBound.value
  if (projectFilterOptions.fundingAmount.length === 0) {
    amountLow.value = minB
    amountHigh.value = maxB
    return
  }
  let low = snapFundingAmountToStep(minB)
  let high = snapFundingAmountToStep(maxB)
  low = Math.min(Math.max(low, minB), maxB)
  high = Math.max(Math.min(high, maxB), minB)
  if (low > high) {
    amountLow.value = minB
    amountHigh.value = maxB
  } else {
    amountLow.value = low
    amountHigh.value = high
  }
  syncAmountRangeToStore()
}

function onYearLowUpdate(value: number): void {
  yearLow.value = Math.min(value, yearHigh.value)
  syncYearRangeToStore()
}

function onYearHighUpdate(value: number): void {
  yearHigh.value = Math.max(value, yearLow.value)
  syncYearRangeToStore()
}

function onAmountLowUpdate(value: number): void {
  const snapped = snapFundingAmountToStep(value)
  amountLow.value = Math.min(Math.max(snapped, amountMinBound.value), amountHigh.value)
  syncAmountRangeToStore()
}

function onAmountHighUpdate(value: number): void {
  const snapped = snapFundingAmountToStep(value)
  amountHigh.value = Math.max(Math.min(snapped, amountMaxBound.value), amountLow.value)
  syncAmountRangeToStore()
}

let debounceTimer: ReturnType<typeof setTimeout> | undefined

async function refreshProjectLayer(): Promise<void> {
  const myMap = myMapStore.myMap
  if (!myMap) return
  const mapInstance = (myMap as { map?: { getSource: (id: string) => unknown } }).map
  const sourceExists = mapInstance?.getSource?.(ItemType.PROJECT)
  if (!sourceExists && (myMapStore.projectLayer?.isShown ?? false)) {
    await AppLayersService.setPlatformDataLayer(ItemType.PROJECT)
  }
  myMap.setData(ItemType.PROJECT, AppLayersService.getGeojsonForMapDisplay(ItemType.PROJECT))
}

async function applyFiltersAndRefresh(): Promise<void> {
  await myMapStore.applyProjectMapFilters()
  await refreshProjectLayer()
}

function onClearFilters(): void {
  myMapStore.clearProjectMapFilters()
  resetYearSlidersToBounds()
  resetAmountSlidersToBounds()
  void refreshProjectLayer()
}

watch(
  isProjectLayerVisibleOnMap,
  async (visible) => {
    if (!visible || projectFilterOptionsLoaded.value) return
    const loaded = await ProjectService.getFilterOptions()
    Object.assign(projectFilterOptions, loaded)
    projectFilterOptionsLoaded.value = true
    await nextTick()
    resetAmountSlidersToBounds()
    resetYearSlidersToBounds()
  },
  { immediate: true },
)

watch(
  [yearMinBound, yearMaxBound],
  () => {
    resetYearSlidersToBounds()
  },
  { immediate: true },
)

watch(
  [amountMinBound, amountMaxBound],
  () => {
    resetAmountSlidersToBounds()
  },
  { immediate: true },
)

watch(
  () => myMapStore.projectMapFilters,
  () => {
    if (!isProjectLayerVisibleOnMap.value) return
    if (debounceTimer != null) {
      clearTimeout(debounceTimer)
    }
    debounceTimer = setTimeout(() => {
      void applyFiltersAndRefresh()
    }, 400)
  },
  { deep: true },
)

onBeforeUnmount(() => {
  if (debounceTimer != null) {
    clearTimeout(debounceTimer)
  }
})
</script>

<style scoped lang="scss">
.MyMapProjectFilters {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin-top: 0.5rem;
  padding-top: 0;
}

/* Same row + label as MyMapOsmCategory__subHeader / __details (e.g. "4 données"). */
.MyMapProjectFilters__titleBar {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  padding: 0.3rem 0.5rem;
  margin: 0.125rem 0 0.25rem;
  border-radius: 0.5rem;
  background: rgba(var(--v-theme-main-blue), 0.06);
}

.MyMapProjectFilters__titleText {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-main-blue), 0.7);
  line-height: 1;
}

.MyMapProjectFilters__fields {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.MyMapProjectFilters__label {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

/* Theme accent: central (teal) vs local / commune (orange) via --v-theme-main-blue. */
.MyMapProjectFilters__fieldCaption {
  font-size: var(--panel-label-size);
  color: rgb(var(--v-theme-main-blue));
}

/* CommuneSearch-like field, compact; --empty = "Tous" option. */
.MyMapProjectFilters__input {
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  border-radius: var(--dim-radius);
  border: 1px solid var(--neutral-border);
  background-color: rgb(var(--v-theme-surface));
  padding: 0.35rem 2rem 0.35rem 0.5rem;
  font-size: var(--panel-label-size);
  min-height: 2rem;
  font-family: inherit;
  font-weight: 500;
  font-style: normal;
  color: rgb(17, 24, 39);
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239ca3af'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-size: 1rem;
  background-position: right 0.35rem center;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &--empty {
    color: rgb(156, 163, 175);
    font-style: italic;
    font-weight: 400;
  }

  &:hover {
    border-color: rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    border-color: rgb(var(--v-theme-main-blue));
    box-shadow: 0 0 0 2px rgba(var(--v-theme-main-blue), 0.12);
  }
}

.MyMapProjectFilters__sliderBlock {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding-top: 0.15rem;
}

.MyMapProjectFilters__sliderValues {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text-secondary, #333);
}

/* Compact variant of MyMapHeader__gptBtn (filled teal pill). */
.MyMapProjectFilters__clear {
  align-self: flex-start;
  margin-top: 0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
  padding: 0 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: inherit;
  border: none;
  border-radius: 0.75rem;
  background-color: var(--color-main);
  color: #fff;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--color-main-hover);
    color: #fff;
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-main-blue));
    outline-offset: 2px;
  }
}
</style>
